import { ANONYMOUS_USERNAME, APIFY_ID_REGEX } from '@apify/consts';

/**
 * List of forbidden usernames. Note that usernames can be used as apify.com/username,
 * so we need to prohibit any username that might be part of our website or confusing in any way.
 *
 * After extending this list, check whether any existing users already hold one of the
 * newly-forbidden usernames. The thorough way is to mirror `isForbiddenUsername` server-side
 * by rebuilding the same regex inside Robo 3T / mongosh — paste the contents of this array
 * verbatim into `var patterns = [...]` and run something like:
 *
 *     var forbiddenRegex = new RegExp('^(anonymous|' + patterns.join('|') + ')$', 'i');
 *     var apifyIdRegex = /^[a-zA-Z0-9]{17}$/;
 *     db.getCollection('users').find({
 *         $or: [{ username: forbiddenRegex }, { username: apifyIdRegex }],
 *     }, { username: 1, 'profile.isPublic': 1, createdAt: 1, lastActivityAt: 1 })
 *     .sort({ username: 1 })
 *     .forEach(function (u) { print(u.username + '\t' + !!(u.profile && u.profile.isPublic)); });
 *
 * For a quick sanity check (literals only, fast) use `$in` with an exact-string array
 * of the new additions instead.
 */
const FORBIDDEN_USERNAMES_REGEXPS = [
    // App routes
    'page-not-found',
    'docs',
    'terms-of-use',
    'about',
    'pricing',
    'privacy-policy',
    'customers',
    'request-form',
    'request-solution',
    'release-notes',
    'jobs',
    'api-reference',
    'video-tutorials',
    'acts',
    'key-value-stores',
    'schedules',
    'account',
    'sign-up',
    'sign-in-discourse',
    'admin',
    'documentation',
    'change-password',
    'enroll-account',
    'forgot-password',
    'reset-password',
    'sign-in',
    'verify-email',
    'live-status',
    'browser-info',
    'webhooks',
    'health-check',
    'api',
    'change-log',
    'dashboard',
    'community',
    'crawlers',
    'ext',
    'schemas',

    // Various strings
    'admin',
    'administration',
    'crawler',
    'act',
    'library',
    'lib',
    'apifier',
    'team',
    'contact',
    'doc',
    'documentation',
    'for-business',
    'for-developers',
    'developers',
    'business',
    'integrations',
    'job',
    'setting',
    'settings',
    'privacy',
    'policy',
    'assets',
    'help',
    'config',
    'configuration',
    'terms',
    'hiring',
    'hire',
    'status',
    'status-page',
    'solutions',
    'support',
    'market',
    'marketplace',
    'download',
    'downloads',
    'username',
    'users',
    'user',
    'login',
    'logout',
    'signin',
    'sign',
    'signup',
    'sign-out',
    'signout',
    'plugins',
    'plug-ins',
    'reset',
    'password',
    'passwords',
    'square',
    'profile-photos',
    'profiles',
    'true',
    'false',
    'js',
    'css',
    'img',
    'images',
    'image',
    'partials',
    'fonts',
    'font',
    'dynamic_templates',
    'app',
    'schedules',
    'community',
    'storage',
    'storages',
    'account',
    'node_modules',
    'bower_components',
    'video',
    'knowledgebase',
    'forum',
    'customers',
    'blog',
    'health-check',
    'health',
    'anim',
    'forum_topics.json',
    'forum_categories.json',
    'me',
    'you',
    'him',
    'she',
    'it',
    'external',
    'actor',
    'crawler',
    'scheduler',
    'api',
    'sdk',
    'puppeteer',
    'webdriver',
    'selenium',
    '(selenium.*webdriver)',
    'undefined',
    'page-analyzer',
    'wp-login.php',
    'welcome.action',
    'echo',
    'proxy',
    'super-proxy',
    'gdpr',
    'case-studies',
    'use-cases',
    'how-to',
    'kb',
    'cookies',
    'cookie-policy',
    'cookies-policy',
    'powered-by',
    'run',
    'runs',
    'actor',
    'actors',
    'act',
    'acts',
    'success-stories',
    'roadmap',
    'join-marketplace',
    'presskit',
    'press-kit',
    'covid-19',
    'covid',
    'covid19',
    'matfyz',
    'ideas',
    'public-actors',
    'resources',
    'partners',
    'affiliate',
    'industries',
    'web-scraping',
    'custom-solutions',
    'solution-provider',
    'alternatives',
    'platform',
    'freelancers',
    'freelancer',
    'partner',
    'preview',
    'templates',
    'data-for-generative-ai',
    'discord',
    'praguecrawl',
    'prague-crawl',
    'bob',
    'ai-agents',
    'reel',
    'video-reel',
    'mcp',
    'mcpc',
    'model-context-protocol',
    'modelcontextprotocol',
    // 'apify.com' intentionally unescaped so "." matches any character, also blocking variants like "apify_com" or "apifyxcom"
    'apify.com',
    'design-kit',
    'press-kit',
    'scrapers',
    'professional-services',
    'challenge',
    'challange',
    '1m-challenge',
    '1m-usd-challenge',
    'contact-sales',
    'creator',
    'creators',
    'creator-plan',
    'request-an-integration',
    'whitepaper',
    'adviser',
    'crawlee',
    'ultimate-scraper',
    'agent-skills',

    // Apify platform resources
    'key-value-store',
    'request-queue',
    'request-queues',
    'build',
    'builds',
    'dataset',
    'datasets',
    'schedule',
    'standby',
    'pay-per-event',
    'pay-per-result',
    'pay-per-usage',
    'ppe',
    'ppr',
    'compute-unit',
    'compute-units',

    // AI / LLM related
    'agent',
    'agents',
    'agentic',
    'llms',
    'llm',
    'openai',
    'anthropic',
    'claude',
    'copilot',
    'mistral',
    'gemini',
    'llama',
    'chatbot',
    'chatgpt',
    'prompt',
    'prompts',
    'embeddings',
    'vectors',
    'rag',

    // Agentic protocols / agentic payments / agent identity
    'agentic-commerce',
    'agentic-economy',
    'agentic-payments',
    'a2a',
    'a2b',
    'a2p',
    'ap2',
    'b2a',
    'acp',
    'ucp',
    'mpp',
    'x402',
    'l402',
    'anp',
    'agp',
    'erc-8004',
    'kya',
    'kyapay',

    // Trust / verification / anti-impersonation handles
    // (entries containing 'apify' are already blocked by USERNAME.RESTRICTED_REGEX)
    'official',
    'verified',
    'certified',
    'authorized',
    'partner-official',
    'gold-partner',
    'premium-partner',
    'staff',
    'moderator',
    'apify-admin',

    // Apify subdomain / service names (current and future)
    'console',
    'cli',
    'studio',
    'gateway',
    'workbench',
    'agents-marketplace',
    'agents-store',
    'mcp-server',
    'mcp-servers',
    'mcp-gateway',

    // Region / locale codes (3+ chars only; 1-2 char codes are blocked by USERNAME.MIN_LENGTH)
    'asia',
    'latam',
    'emea',
    'apac',
    'i18n',
    'l10n',
    'localization',
    'localisation',

    // Currency / payment generics (open standards, not company brands)
    'usd',
    'eur',
    'gbp',
    'jpy',
    'cny',
    'usdc',
    'usdt',
    'btc',
    'eth',
    'sol',
    'crypto',
    'fiat',
    'stablecoin',
    'stablecoins',
    'payment',
    'payments',
    'payout',
    'payouts',
    'invoice',
    'invoices',
    'wallet',

    // Compliance / certifications
    'soc',
    'soc2',
    'iso',
    'iso-27001',
    'hipaa',
    'ccpa',
    'pci',
    'pci-dss',
    'dpa',
    'dpia',
    'dmca',
    'sla',
    'eula',
    'aml',
    'kyc',

    // Open-source / community roles
    'contributor',
    'contributors',
    'maintainer',
    'maintainers',
    'core-team',
    'oss',
    'open-source',
    'opensource',
    'open-source-software',

    // Anti-abuse / scam-bait names (frequently used in account fraud)
    'spam',
    'scam',
    'phishing',
    'malware',
    'hack',
    'hacked',
    'giveaway',
    'airdrop',
    'bitcoin-giveaway',
    'free-credits',
    'free-money',

    // Apify marketing / campaign slugs
    'launch',
    'launches',
    'webinar',
    'webinars',
    'meetup',
    'meetups',
    'summit',
    'conference',
    'conferences',

    // Organizations / workspaces / permissions
    'org',
    'orgs',
    'organisation',
    'workspace',
    'workspaces',
    'tenant',
    'tenants',
    'role',
    'roles',
    'permission',
    'permissions',

    // Auth / security
    'saml',
    'scim',
    'token',
    'tokens',
    'apikey',
    'api-key',
    'api-keys',
    'keys',
    'secret',
    'secrets',

    // Billing / commerce
    'coupon',
    'coupons',
    'discount',
    'discounts',
    'promos',
    'refund',
    'refunds',
    'credit',
    'credits',
    'billing-portal',

    // Infrastructure / environments
    'production',
    'prod',
    'canary',
    'internal',
    'restricted',

    // Communication
    'conversations',
    'reactions',
    'mentions',
    'channel',
    'channels',
    'inbox',
    'no-reply',
    'noreply',

    // Legal / trust / web standards
    'accessibility',
    'imprint',
    'impressum',
    'trust',
    'trust-center',
    'security-center',

    // Brand protection / impersonation prevention
    'brand',
    'branding',
    'support-team',
    'abuse',

    // Incidents / updates
    'outage',
    'incident',
    'incidents',
    'what-is-new',
    'whatsnew',

    // Special files
    'index',
    'index\\.html',
    '(favicon\\.[a-z]+)',
    'BingSiteAuth.xml',
    '(google.+\\.html)',
    'robots\\.txt',
    '(sitemap\\.[a-z]+)',
    '(apple-touch-icon.*)',
    'security-whitepaper\\.pdf',
    'security\\.txt',
    'llms\\.txt',
    'llms-full\\.txt',
    'AGENTS\\.md',
    'CLAUDE\\.md',
    'auth\\.md',

    // All hidden files
    '(\\..*)',

    // Username starting with xxx-
    '(xxx-.*)',

    // Usernames containing inappropriate/adult content keywords.
    // Each entry requires non-letter boundaries on both sides so the keyword only
    // matches as a whole word — this avoids false positives like Akshit (shit),
    // Hancock (cock), Nudelman (nude), Uranus (anus), pussycat (pussy),
    // snaked (naked), scunthorpe (cunt), christoph-dick-media (dick — legitimate
    // German surname, not blocked), etc.
    '((.+[^a-z]|)porn([^a-z].+|))',
    '((.+[^a-z]|)vagina([^a-z].+|))',
    '((.+[^a-z]|)dildo([^a-z].+|))',
    '((.+[^a-z]|)nsfw([^a-z].+|))',
    '((.+[^a-z]|)hentai([^a-z].+|))',
    '((.+[^a-z]|)cunt([^a-z].+|))',
    '((.+[^a-z]|)fuck([^a-z].+|))',
    '((.+[^a-z]|)shit([^a-z].+|))',
    '((.+[^a-z]|)bitch([^a-z].+|))',
    '((.+[^a-z]|)slut([^a-z].+|))',
    '((.+[^a-z]|)whore([^a-z].+|))',
    '((.+[^a-z]|)boob([^a-z].+|))',
    '((.+[^a-z]|)tits([^a-z].+|))',
    '((.+[^a-z]|)cock([^a-z].+|))',
    '((.+[^a-z]|)anus([^a-z].+|))',
    '((.+[^a-z]|)pussy([^a-z].+|))',
    '((.+[^a-z]|)nude([^a-z].+|))',
    '((.+[^a-z]|)penis([^a-z].+|))',
    '((.+[^a-z]|)naked([^a-z].+|))',

    // Username starting with install-cli. (see https://docs.apify.com/cli/docs/installation)
    '(install-cli\\..*)',

    // Strings not starting with letter or number
    '([^0-9a-z].*)',

    // Strings not ending with letter or number
    '(.*[^0-9a-z])',

    // Strings where there's more than one underscore, comma or dash in row
    '(.*[_.\\-]{2}.*)',

    // Reserved usernames from https://github.com/shouldbee/reserved-usernames/blob/master/reserved-usernames.json
    '0',
    'about',
    'access',
    'account',
    'accounts',
    'activate',
    'activities',
    'activity',
    'ad',
    'add',
    'address',
    'adm',
    'admin',
    'administration',
    'administrator',
    'ads',
    'adult',
    'advertising',
    'affiliate',
    'affiliates',
    'ajax',
    'all',
    'alpha',
    'analysis',
    'analytics',
    'android',
    'anon',
    'anonymous',
    'api',
    'app',
    'apps',
    'archive',
    'archives',
    'article',
    'asct',
    'asset',
    'atom',
    'auth',
    'authentication',
    'avatar',
    'backup',
    'balancer-manager',
    'banner',
    'banners',
    'beta',
    'billing',
    'bin',
    'blog',
    'blogs',
    'board',
    'book',
    'bookmark',
    'bot',
    'bots',
    'bug',
    'business',
    'cache',
    'cadastro',
    'calendar',
    'call',
    'campaign',
    'cancel',
    'captcha',
    'career',
    'careers',
    'cart',
    'categories',
    'category',
    'cgi',
    'cgi-bin',
    'changelog',
    'chat',
    'check',
    'checking',
    'checkout',
    'client',
    'cliente',
    'clients',
    'code',
    'codereview',
    'comercial',
    'comment',
    'comments',
    'communities',
    'community',
    'company',
    'compare',
    'compras',
    'config',
    'configuration',
    'connect',
    'contact',
    'contact-us',
    'contact_us',
    'contactus',
    'contest',
    'contribute',
    'corp',
    'create',
    'css',
    'dashboard',
    'data',
    'db',
    'default',
    'delete',
    'demo',
    'design',
    'designer',
    'destroy',
    'dev',
    'devel',
    'developer',
    'developers',
    'diagram',
    'diary',
    'dict',
    'dictionary',
    'die',
    'dir',
    'direct_messages',
    'directory',
    'dist',
    'doc',
    'docs',
    'documentation',
    'domain',
    'download',
    'downloads',
    'ecommerce',
    'edit',
    'editor',
    'edu',
    'education',
    'email',
    'employment',
    'empty',
    'end',
    'enterprise',
    'entries',
    'entry',
    'error',
    'errors',
    'eval',
    'event',
    'events',
    'exit',
    'explore',
    'facebook',
    'faq',
    'favorite',
    'favorites',
    'feature',
    'features',
    'feed',
    'feedback',
    'feeds',
    'file',
    'files',
    'first',
    'flash',
    'fleet',
    'fleets',
    'flog',
    'follow',
    'followers',
    'following',
    'forgot',
    'form',
    'forum',
    'forums',
    'founder',
    'free',
    'friend',
    'friends',
    'ftp',
    'gadget',
    'gadgets',
    'game',
    'games',
    'get',
    'gift',
    'gifts',
    'gist',
    'github',
    'graph',
    'group',
    'groups',
    'guest',
    'guests',
    'help',
    'home',
    'homepage',
    'host',
    'hosting',
    'hostmaster',
    'hostname',
    'howto',
    'hpg',
    'html',
    'http',
    'httpd',
    'https',
    'i',
    'iamges',
    'icon',
    'icons',
    'id',
    'idea',
    'ideas',
    'image',
    'images',
    'imap',
    'img',
    'index',
    'indice',
    'info',
    'information',
    'inquiry',
    'instagram',
    'intranet',
    'invitations',
    'invite',
    'ipad',
    'iphone',
    'irc',
    'is',
    'issue',
    'issues',
    'it',
    'item',
    'items',
    'java',
    'javascript',
    'job',
    'jobs',
    'join',
    'js',
    'json',
    'jump',
    'knowledgebase',
    'language',
    'languages',
    'last',
    'ldap-status',
    'legal',
    'license',
    'link',
    'links',
    'linux',
    'list',
    'lists',
    'log',
    'log-in',
    'log-out',
    'log_in',
    'log_out',
    'login',
    'logout',
    'logs',
    'm',
    'mac',
    'mail',
    'mail1',
    'mail2',
    'mail3',
    'mail4',
    'mail5',
    'mailer',
    'mailing',
    'maintenance',
    'manager',
    'manual',
    'map',
    'maps',
    'marketing',
    'master',
    'me',
    'media',
    'member',
    'members',
    'message',
    'messages',
    'messenger',
    'microblog',
    'microblogs',
    'mine',
    'mis',
    'mob',
    'mobile',
    'movie',
    'movies',
    'mp3',
    'msg',
    'msn',
    'music',
    'musicas',
    'mx',
    'my',
    'mysql',
    'name',
    'named',
    'nan',
    'navi',
    'navigation',
    'net',
    'network',
    'new',
    'news',
    'newsletter',
    'nick',
    'nickname',
    'notes',
    'noticias',
    'notification',
    'notifications',
    'notify',
    'ns',
    'ns1',
    'ns10',
    'ns2',
    'ns3',
    'ns4',
    'ns5',
    'ns6',
    'ns7',
    'ns8',
    'ns9',
    'null',
    'oauth',
    'oauth_clients',
    'offer',
    'offers',
    'official',
    'old',
    'online',
    'openid',
    'operator',
    'order',
    'orders',
    'organization',
    'organizations',
    'overview',
    'owner',
    'owners',
    'page',
    'pager',
    'pages',
    'panel',
    'password',
    'payment',
    'perl',
    'phone',
    'photo',
    'photoalbum',
    'photos',
    'php',
    'phpmyadmin',
    'phppgadmin',
    'phpredisadmin',
    'pic',
    'pics',
    'ping',
    'plan',
    'plans',
    'plugin',
    'plugins',
    'policy',
    'pop',
    'pop3',
    'popular',
    'portal',
    'post',
    'postfix',
    'postmaster',
    'posts',
    'pr',
    'premium',
    'press',
    'price',
    'pricing',
    'privacy',
    'privacy-policy',
    'privacy_policy',
    'privacypolicy',
    'private',
    'product',
    'products',
    'profile',
    'project',
    'projects',
    'promo',
    'pub',
    'public',
    'purpose',
    'put',
    'python',
    'query',
    'random',
    'ranking',
    'read',
    'readme',
    'recent',
    'recruit',
    'recruitment',
    'register',
    'registration',
    'release',
    'remove',
    'replies',
    'report',
    'reports',
    'repositories',
    'repository',
    'req',
    'request',
    'requests',
    'reset',
    'roc',
    'root',
    'rss',
    'ruby',
    'rule',
    'sag',
    'sale',
    'sales',
    'sample',
    'samples',
    'save',
    'school',
    'script',
    'scripts',
    'search',
    'secure',
    'security',
    'self',
    'send',
    'server',
    'server-info',
    'server-status',
    'service',
    'services',
    'session',
    'sessions',
    'setting',
    'settings',
    'setup',
    'share',
    'shop',
    'show',
    'sign-in',
    'sign-up',
    'sign_in',
    'sign_up',
    'signin',
    'signout',
    'signup',
    'site',
    'sitemap',
    'sites',
    'smartphone',
    'smtp',
    'soporte',
    'source',
    'spec',
    'special',
    'sql',
    'src',
    'ssh',
    'ssl',
    'ssladmin',
    'ssladministrator',
    'sslwebmaster',
    'staff',
    'stage',
    'staging',
    'start',
    'stat',
    'state',
    'static',
    'stats',
    'status',
    'store',
    'stores',
    'stories',
    'style',
    'styleguide',
    'stylesheet',
    'stylesheets',
    'subdomain',
    'subscribe',
    'subscription',
    'subscriptions',
    'suporte',
    'support',
    'svn',
    'swf',
    'sys',
    'sysadmin',
    'sysadministrator',
    'system',
    'tablet',
    'tablets',
    'tag',
    'talk',
    'task',
    'tasks',
    'team',
    'teams',
    'tech',
    'telnet',
    'term',
    'terms',
    'terms-of-service',
    'terms_of_service',
    'termsofservice',
    'test',
    'test1',
    'test2',
    'test3',
    'teste',
    'testing',
    'tests',
    'theme',
    'themes',
    'thread',
    'threads',
    'tmp',
    'todo',
    'tool',
    'tools',
    'top',
    'topic',
    'topics',
    'tos',
    'tour',
    'translations',
    'trends',
    'tutorial',
    'tux',
    'tv',
    'twitter',
    'undef',
    'unfollow',
    'unsubscribe',
    'update',
    'upload',
    'uploads',
    'url',
    'usage',
    'user',
    'username',
    'users',
    'usuario',
    'vendas',
    'ver',
    'version',
    'video',
    'videos',
    'visitor',
    'watch',
    'weather',
    'web',
    'webhook',
    'webhooks',
    'webmail',
    'webmaster',
    'website',
    'websites',
    'welcome',
    'widget',
    'widgets',
    'wiki',
    'win',
    'windows',
    'word',
    'work',
    'works',
    'workshop',
    'ww',
    'wws',
    'www',
    'www1',
    'www2',
    'www3',
    'www4',
    'www5',
    'www6',
    'www7',
    'wwws',
    'wwww',
    'xfn',
    'xml',
    'xmpp',
    'xpg',
    'xxx',
    'yaml',
    'year',
    'yml',
    'you',
    'yourdomain',
    'yourname',
    'yoursite',
    'yourusername',
];

// Regex matching forbidden usernames.
const FORBIDDEN_REGEXP = new RegExp(`^(${ANONYMOUS_USERNAME}|${FORBIDDEN_USERNAMES_REGEXPS.join('|')})$`, 'i');

/**
 * Checks whether username is listed in the forbidden username list
 * or looks like an Apify ID.
 *
 * NOTE: The forbidden list may expand over time, so a username that was
 * previously permitted may later become forbidden. Use this function only
 * when validating a username change, not as part of normal authentication
 * or authorization flow.
 */
export function isForbiddenUsername(username: string): boolean {
    return !!username.match(APIFY_ID_REGEX) || !!username.match(FORBIDDEN_REGEXP);
}
